<script>
    import LoginWindow from "./login-window.vue";
    import LoginFooterIcons from "./login-footer-icons.vue";
    import LoginContainer from "./login-container.vue";
    import LoginBanner from "./login-banner.vue";
    import Loading from "../common/loading.vue";
    import LoginWindowFooter from "./login-window-footer.vue";
    import LoginEmailField from "./login-email-field.vue";
    import LoginForm from "./login-form.vue";
    import LoginSubmitButton from "./login-submit-button.vue";
    import PasswordFields from "../common/password-fields.vue";
    import SelectField from "../common/select-field.vue";
    import TextField from "../common/text-field.vue";
    import API from "../../API";

    export default {
        name: "reset",
        components: {
            TextField,
            SelectField,
            PasswordFields,
            LoginSubmitButton,
            LoginForm,
            LoginEmailField,
            LoginWindowFooter, Loading, LoginBanner, LoginContainer, LoginFooterIcons, LoginWindow},
        props: [
            'versionText',
            'resetToken',
            'csrfToken',
            'appKey',
            'type',
            'url',
            'customer_external_id',
            'session',
            'errors',
            'license_server_active',
            'email',
        ],
        data: function () {
            return {
                old: {},
                inner_session: {},
                link: '0',
                previousEmail: '',
                productkey: '',
                license_user: '',
                license_password: '',
                inner_customer_external_id: '',

                password: null,
                passwordConfirmation: null,
                emailValid: false,
                showPw: false,
                loading: false,
                tokenRequested: false
            }
        },
        provide() {
            return {
                appKey: this.appKey,
            }
        },
        computed: {
            action_url: function () {
                return this.url + '/password/' + (this.type === 'set' ? 'set' : 'reset') + (this.appKey ? ('/' + this.appKey) : '');
            },
            linkOptions() {
                return [
                    {value: '0', text: this.translations.no_link},
                    {value: '1', text: this.translations.link_pk},
                    {value: '2', text: this.translations.link_credentials},
                ]
            },
            /**
             * @returns {String}
             */
            title() {
                return (this.type === 'set') ? this.translations.setPassword : this.translations.forgotPassword;
            },
            errorMessages() {
                const parsed = JSON.parse(this.errors);
                if (this.errors === '[]') return [];
                const properties = Object.getOwnPropertyNames(parsed);
                return properties.map(property => parsed[property]);
            },
            hasTokenError() {
                return this.errorMessages.some(messages => {
                    return messages.some(message => message.includes('token is invalid'));
                });
            },
            passwordsValid() {
                const isMatching = this.password === this.passwordConfirmation;
                const isLongEnough = this.password?.length >= 8 || this.passwordConfirmation?.length >= 8;
                const regEx = new RegExp(/^(?=.*[A-Za-z])(?=.*\d).{8,}$/);
                const isComplexEnough = regEx.test(this.password) && regEx.test(this.passwordConfirmation);

                return isMatching && isLongEnough && isComplexEnough;
            },
            translations() {
                return {
                    ewi: Lang.langGet("EWI"),
                    setPassword: Lang.langGet("Set password"),
                    forgotPassword: Lang.langGet("Forgot password"),
                    password: Lang.langGet("Password"),
                    confirm_password: Lang.langGet("Confirm Password"),
                    email: Lang.langGet('Email address'),
                    no_link: Lang.langGet("Do not link with license portal"),
                    link_pk: Lang.langGet("Use product key to link with license portal"),
                    link_credentials: Lang.langGet("Use credentials to link with license portal"),
                    productkey: Lang.langGet("Product Key"),
                    license_user: Lang.langGet("License portal username"),
                    license_password: Lang.langGet("License portal password"),
                    requestNewToken: Lang.langGet("Request new token"),
                    tokenRequested: Lang.langGet("A new token has been requested. Check your emails.")
                }
            }
        },
        methods: {
            async handleRequestNewToken() {
                this.loading = true
                const api = new API('/password/email');
                const data = {
                    _token: this.csrfToken,
                    email: this.previousEmail
                };
                const response = await api.post(data);

                this.tokenRequested = true;
                this.loading = false;
            },
            handlePasswordChange(prop, value) {
                this[prop] = value;
            },
            handleValidity(prop, value) {
                this[`${prop}Valid`] = value;
            },
            handleSubmit() {
                this.loading = true;
            },
        },
        mounted: function () {
            this.inner_session = JSON.parse(this.session);

            this.old = this.inner_session._old_input ?? [];
            this.link = this.old['link'] ?? '0';
            this.previousEmail = this.old['email'] ?? this.email;
            this.productkey = this.old['productkey'] ?? '';
            this.license_user = this.old['license_user'] ?? '';
            this.license_password = this.old['license_password'] ?? '';

            this.inner_customer_external_id = (this.old.hasOwnProperty('customer_external_id')
                ? this.old['customer_external_id']
                : this.customer_external_id) ?? '';
        }
    }
</script>

<template>
    <login-container>
        <loading :loading="loading"/>
        <login-window>
            <login-banner :title="title" />

            <div v-if="!tokenRequested" class="mb-2">
                <div v-for="errors in errorMessages" class="login-error">
                    <div v-for="error in errors">
                        {{error}}
                    </div>
                </div>
            </div>

            <div v-if="tokenRequested" class="alert alert-success">
                {{ translations.tokenRequested }}
            </div>

            <v-btn v-if="hasTokenError" class="btn btn-primary full-width" @click="handleRequestNewToken" :disabled="tokenRequested">
                {{translations.requestNewToken}}
            </v-btn>
            <login-form v-else :url="action_url">
                <input type="hidden" name="token" :value="resetToken" />
                <input type="hidden" name="_token" :value="csrfToken" />
                <input type="hidden" name="customer_external_id" :value="inner_customer_external_id">
                <login-email-field @valid="handleValidity" validateOnBlur :value="previousEmail" />
                <password-fields
                    asColumn
                    :password="password"
                    :passwordConfirmation="passwordConfirmation"
                    @input="handlePasswordChange"
                />
                <div v-if="inner_customer_external_id === '' && license_server_active === '1'">
                    <select-field
                        :value="link"
                        @input="value => this.link = value"
                        :items="linkOptions"
                        name="link"
                    />
                    <v-text-field v-if="link==='1'"
                        v-model="productkey"
                        name="productkey"
                        :placeholder="translations.productkey + ': XXXXX-XXXXX-XXXXX-XXXXXX-XXXXXX'"
                        required outlined dense
                    />
                    <template v-if="link==='2'">
                        <v-text-field
                            name="license_user"
                            :placeholder="translations.license_user"
                            v-model="license_user"
                            dense outlined required
                        />
                        <v-text-field
                            name="license_password"
                            :placeholder="translations.license_password"
                            v-model="license_password"
                            dense outlined required
                            :type="showPw ? 'text' : 'password'"
                            :append-icon="showPw ? 'mdi-eye' : 'mdi-eye-off'"
                            @click:append="showPw = !showPw"
                        />
                    </template>
                </div>

                <login-submit-button @click="handleSubmit" :disabled="!passwordsValid || !emailValid">
                    {{ translations.setPassword }}
                </login-submit-button>
            </login-form>

            <login-window-footer :versionText="versionText"/>
        </login-window>
        <login-footer-icons url="https://easescreen.com/"/>
    </login-container>
</template>
